<template>
  <div
    class="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[3px] sm:p-6"
    @click.self="close"
  >
    <div
      class="modal-content relative w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-black/[0.04] animate-scale-in dark:border-gray-700/90 dark:bg-gray-900 dark:shadow-black/40 dark:ring-white/[0.06]"
      @click.stop
    >
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"
        aria-hidden="true"
      />

      <header
        class="modal-header flex items-start gap-4 border-b border-gray-100 px-6 pb-4 pt-5 dark:border-gray-800"
      >
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/15 to-teal-600/20 text-emerald-600 dark:from-emerald-400/20 dark:to-teal-500/15 dark:text-emerald-400"
        >
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <div class="min-w-0 flex-1 pt-0.5">
          <h2 class="modal-title text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
            {{ t('kanban.createLead.title') }}
          </h2>
          <p class="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {{ t('kanban.createLead.subtitle') }}
          </p>
        </div>
        <button
          type="button"
          class="modal-close shrink-0 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          :aria-label="t('common.close')"
          @click="close"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <form @submit.prevent="createLead" class="modal-form space-y-5 px-6 py-5">
        <div class="form-group space-y-2">
          <label class="form-label text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {{ t('kanban.createLead.nameLabel') }} <span class="text-rose-500">*</span>
          </label>
          <input
            ref="nameInput"
            v-model="form.nome"
            type="text"
            class="form-input"
            :placeholder="t('kanban.createLead.namePlaceholder')"
            required
          />
        </div>

        <div
          class="modal-actions flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/60 px-0 pb-0 pt-5 dark:border-gray-800 dark:bg-gray-950/35 sm:flex-row sm:justify-end sm:gap-3 sm:bg-transparent sm:px-0 sm:pt-4"
        >
          <button
            type="button"
            class="btn-secondary inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            @click="close"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            class="btn-primary inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/25 transition-all hover:from-emerald-500 hover:to-teal-500 hover:shadow-emerald-500/30 disabled:pointer-events-none disabled:opacity-50"
            :disabled="loading"
          >
            <span v-if="loading" class="loading-spinner" />
            {{ loading ? t('kanban.createLead.creating') : t('kanban.createLead.createButton') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'

export default {
  name: 'CreateLeadModal',
  props: {
    workspaceId: {
      type: Number,
      required: true
    },
    columns: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const loading = ref(false)
    const nameInput = ref(null)

    const form = reactive({
      nome: ''
    })

    const close = () => {
      emit('close')
    }

    const createLead = async () => {
      try {
        loading.value = true

        const primeiraColuna =
          props.columns && props.columns.length > 0 ? props.columns[0].id : null

        if (!primeiraColuna) {
          alert(t('kanban.createLead.noColumns'))
          return
        }

        const leadData = {
          nome: form.nome,
          workspaceId: props.workspaceId,
          colunaId: primeiraColuna,
          status: 'NOVO'
        }

        console.log('Criando lead na primeira coluna (Backlog):', leadData)

        const response = await api.post('/leads', leadData)

        console.log('Lead criado com sucesso:', response.data)

        form.nome = ''
        emit('created', response.data)
        close()
      } catch (error) {
        console.error('Erro ao criar lead:', error)
        alert(t('kanban.createLead.errorGeneric'))
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      nextTick(() => {
        if (nameInput.value) {
          nameInput.value.focus()
        }
      })
    })

    return {
      t,
      loading,
      form,
      nameInput,
      close,
      createLead
    }
  }
}
</script>

<style scoped>
.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:hover {
  border-color: var(--border-hover);
  background-color: var(--bg-hover);
}

.form-input:focus {
  outline: none;
  border-color: var(--info);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--info) 22%, transparent);
}

.loading-spinner {
  @apply h-4 w-4 shrink-0 rounded-full border-2 border-white border-t-transparent animate-spin;
}
</style>
